import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { isEmpty, isNull } from "lodash";
import { Subscription } from "rxjs";
import { CustomValidationService } from "src/app/cbta-web/services/custom-validation.service";
import { Rank, RankLabels } from "src/app/models/enum";
import { FormDataOptions, Profile, ProfileFormData, User } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import {
	PROFILE_IMAGE_UNREADABLE,
	prepareProfileImageFile,
} from "src/app/shared/utils/profile-image.utils";
import { HelperService } from "src/app/util/helper.service";

@Component({
	selector: "app-profile",
	templateUrl: "./profile.component.html",
	styleUrls: ["./profile.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ProfileComponent implements OnInit, OnDestroy {
	profilePreview?: string = "";
	signaturePreview?: string = "";
	signatureFile?: File;

	profileForm!: UntypedFormGroup;

	private hasChanges: boolean = false;

	inEditMode: boolean = false;

	profileSub!: Subscription;
	profileFormSub!: Subscription;

	private profile?: Profile;
	private user?: User;
	private userId: string = "";

	initialValue?: ProfileFormData;

	rankControl = new UntypedFormControl("");
	licenseTypeControl = new UntypedFormControl("");

	showHide: boolean = false;
	hasSignOffPin = false;
	readonly signOffPinMask = "......";
	private signOffPinFetched = false;
	private signOffPinLoading = false;
	private signOffPinRevealFailed = false;
	private signOffPinSub?: Subscription;

	get showSignOffPinMask(): boolean {
		return (
			this.hasSignOffPin && !this.signOffPinFetched && !this.signOffPinRevealFailed
		);
	}

	private resetSignOffPinReveal(): void {
		this.showHide = false;
		this.signOffPinFetched = false;
		this.profileForm.patchValue({ secretKey: "" }, { emitEvent: false });
	}

	private profilePreviewObjectUrl?: string;

	@ViewChild("profilePictureRef")
	profilePictureRef?: ElementRef<HTMLInputElement>;

	constructor(
		private dialog: MatDialog,
		private httpService: HttpService,
		private roleService: RoleService,
		private loaderService: LoaderService,
		private formBuilder: UntypedFormBuilder,
		public sharedService: SharedService,
		private helperService: HelperService,
		private profileService: ProfileService,
		public customValidationService: CustomValidationService,
	) {}

	ngOnInit(): void {
		this.initializeForm();
		this.initUser();
	}

	initializeForm(): void {
		this.profileForm = this.formBuilder.group({
			name: ["", Validators.required],
			// initials: ['', Validators.required],
			email: ["", Validators.required],
			aoc: [""],
			organization: ["", Validators.required],
			designation: [""],
			rank: ["", Validators.required],
			licenseNumber: [""],
			licenseType: [""],
			// instructorLicenseNumber: [''],
			examinerNumber: [""],
			secretKey: [""],
			staffNumber: ["", Validators.required],
		});

		this.setEditMode(false);
	}

	toggleShowHide(): void {
		if (!this.hasSignOffPin) {
			this.showHide = !this.showHide;
			return;
		}

		if (this.showHide) {
			this.resetSignOffPinReveal();
			return;
		}

		if (this.signOffPinFetched) {
			this.showHide = true;
			return;
		}

		if (this.signOffPinLoading) {
			return;
		}

		this.signOffPinRevealFailed = false;
		this.signOffPinLoading = true;
		this.signOffPinSub?.unsubscribe();
		this.signOffPinSub = this.httpService.getMySignOffPin().subscribe({
			next: (result) => {
				this.signOffPinLoading = false;
				const pin = result?.data?.secret_key ?? "";
				this.profileForm.patchValue({ secretKey: pin }, { emitEvent: false });
				this.signOffPinFetched = true;
				this.showHide = true;
			},
			error: (error) => {
				this.signOffPinLoading = false;
				this.resetSignOffPinReveal();
				this.signOffPinRevealFailed = true;
				console.error("Failed to load Sign Off PIN:", error);
			},
		});
	}

	ngOnDestroy(): void {
		if (this.profileSub) {
			this.profileSub.unsubscribe();
		}

		if (this.profileFormSub) {
			this.profileFormSub.unsubscribe();
		}

		this.signOffPinSub?.unsubscribe();

		this.revokeProfilePreviewObjectUrl();
	}

	initUser() {
		this.signOffPinFetched = false;
		this.showHide = false;
		this.hasSignOffPin = false;
		this.signOffPinLoading = false;
		this.signOffPinRevealFailed = false;
		this.signOffPinSub?.unsubscribe();

		const userDetails = this.roleService.userDetails;

		this.user = userDetails;
		const userId = userDetails.id;
		this.userId = userId;

		if (userId) {
			this.loaderService.setLoading(true);
			this.profileSub = this.httpService.getProfile(userId).subscribe(
				(result) => {
					const profile = result?.data as Profile;

					this.profile = profile;

					// TODO: initialize profile values
					this.initFormValues(profile);

					this.loaderService.setLoading(false);
				},
				(error) => {
					console.error(`No result for User(${userId}) :`, error);

					this.initNoResultValues();
					this.loaderService.setLoading(false);
				},
			);
		} else {
			this.initNoResultValues();
		}
	}

	get hasProfile(): boolean {
		return this.profileService.hasProfile;
	}

	/**
	 * Sets form values if no profile details found
	 */
	initNoResultValues() {
		this.hasSignOffPin = false;

		const user = this.roleService.userDetails;

		const data: ProfileFormData = {
			name: user.name ?? "",
			email: user.email,
			// initials: '',
			licenseNumber: "",
			licenseType: "",
			// instructorLicenseNumber: '',
			examinerNumber: "",
			organization: "",
			designation: "",
			secretKey: "",
			staffNumber: "",
			rank: "",
		};

		// this.profilePreview = user.picture ?? "";

		if (isEmpty(this.initialValue)) {
			this.initialValue = data;
		}

		this.setFormValue(data);

		this.setEditMode(true);

		this.detectFormChanges();
	}

	/**
	 * Sets from values based on Profile details fetched
	 * @param {Profile} profile
	 */
	initFormValues(profile: Profile) {
		this.hasSignOffPin = !!profile?.has_sign_off_pin;

		const data: ProfileFormData = {
			name: profile?.user.name ?? "",
			// initials: profile?.initials ?? '',
			email: profile?.user?.email ?? "",
			organization: profile?.organisation ?? "",
			designation: profile?.designation ?? "",
			licenseNumber: profile.pilot_license_number ?? "",
			licenseType: profile?.licenseType ?? "",
			examinerNumber: profile?.examiner_number ?? "",
			aoc: profile?.aoc,
			secretKey: "",
			staffNumber: profile?.staff_number,
			rank: profile?.rank ?? "",
		};

		this.profilePreview = profile?.image_name ? profile?.image_signed_url : "assets/no-image.jpg";

		const signatureData = profile?.signature?.data;
		this.signaturePreview = this.helperService.bufferDataToImageUrl(signatureData);
		this.signatureFile = signatureData ? this.helperService.bufferDataToFile(signatureData) : undefined;

		if (isEmpty(this.initialValue)) {
			this.initialValue = data;
		}

		this.setFormValue(data);

		this.detectFormChanges();
	}

	detectFormChanges() {
		this.profileFormSub = this.profileForm.valueChanges.subscribe(() => {
			// Use getRawValue() to include disabled form controls (non-editable fields) in comparison
			const hasChanges = !this.isEqual(this.initialValue, this.profileForm.getRawValue());
			this.setHasChanges(hasChanges);
		});
	}

	/**
	 * Checks if 2 objects are equal key by key
	 * @param obj1
	 * @param obj2
	 * @returns
	 */
	isEqual(obj1: any, obj2: any): boolean {
		const keys1 = Object.keys(obj1);
		const keys2 = Object.keys(obj2);

		// If the number of keys is different, objects are not equal
		if (keys1.length !== keys2.length) {
			return false;
		}

		// Check each key
		for (const key of keys1) {
			if (key === "rank" || key === "licenseType") {
				if (obj1[key] !== obj2[key]?.code) {
					return false;
				}
			} else {
				if (obj1[key] !== obj2[key]) {
					return false;
				}
			}
		}

		return true;
	}

	getRankLabels(rank?: Rank): string {
		if (!isEmpty(rank)) {
			return RankLabels[rank!];
		} else {
			return "";
		}
	}

	get name(): string {
		return this.profile?.user?.name ?? this.user?.name ?? "";
	}

	get isInstructor() {
		return this.roleService.hasInstructorView;
	}

	setFormValue(data: ProfileFormData) {
		this.profileForm.patchValue(data);

		// Only set rank if it exists and is found in options
		if (data.rank && !isEmpty(data.rank)) {
			const rank = this.sharedService.dropDownOptionsRank.find((r) => r.code === data.rank);
			if (rank) {
				this.saveRank({
					code: `${data.rank}`,
					description: `${rank.description}`,
				});
			} else {
				// If rank code not found in options, leave it empty
				this.rankControl.setValue(null);
				this.rankField?.patchValue(null);
			}
		} else {
			// If no rank data, leave it empty
			this.rankControl.setValue(null);
			this.rankField?.patchValue(null);
		}

		// Set license type - convert string to object format for autocomplete
		// Use the same options source as the template (sharedService.dropDownOptionsLicensTypes)
		// Use setTimeout to ensure this happens after form patching and component initialization
		setTimeout(() => {
			if (data.licenseType) {
				const licenseTypeOptions = this.sharedService.dropDownOptionsLicensTypes;
				const licenseType = licenseTypeOptions.find((lt) => lt.code === data.licenseType);
				if (licenseType) {
					this.saveLicenseType({
						code: `${data.licenseType}`,
						description: `${licenseType.description}`,
					});
				} else {
					// If not found in options, still set it as an object
					this.saveLicenseType({
						code: `${data.licenseType}`,
						description: `${data.licenseType}`,
					});
				}
			} else {
				// Clear license type control if no value
				this.licenseTypeControl.setValue(null);
				this.licenseTypeField?.patchValue(null);
			}
		}, 0);
	}

	get getProfile() {
		return this.profile;
	}

	get hasProfilePreview(): boolean {
		return !isEmpty(this.profilePreview) && !isNull(this.profilePreview);
	}

	get hasSignaturePreview(): boolean {
		return !isEmpty(this.signaturePreview) && !isNull(this.signaturePreview);
	}

	setEditMode(editMode: boolean): void {
		this.inEditMode = editMode;
		// Fields that should always be disabled (non-editable)
		const nonEditableFields = ["name", "email", "staffNumber", "aoc", "organization", "rank"];

		const controlNames = Object.keys(this.profileForm.controls);
		for (const controlName of controlNames) {
			const control = this.profileForm.get(controlName);
			// Always keep non-editable fields disabled
			if (nonEditableFields.includes(controlName)) {
				control?.disable();
				if (controlName === "rank") {
					this.rankControl?.disable();
				}
			} else {
				// Enable/disable other fields based on edit mode
				if (editMode) {
					control?.enable();
					if (controlName === "licenseType") {
						this.licenseTypeControl?.enable();
					}
				} else {
					control?.disable();
					if (controlName === "licenseType") {
						this.licenseTypeControl?.disable();
					}
				}
			}
		}
	}

	setHasChanges(hasChanges: boolean): void {
		this.hasChanges = hasChanges;
	}

	resetForm(): void {
		if (!isEmpty(this.initialValue)) {
			this.setFormValue(this.initialValue);
		}
		this.setEditMode(false);
		this.setHasChanges(false);
	}

	showConfirmationModal(title: string, description: string, confirm: Function, cancel: Function = () => {}): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "confirm-update-profile-modal",
			height: "200px",
			width: "380px",
			hasBackdrop: true,
			data: {
				modalTitle: title,
				modalContent: description,
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});
		// listen to modal after closing
		const sub = dialogRef.afterClosed().subscribe((result) => {
			if (result && result.close) {
				return confirm();
			}
			sub.unsubscribe();
			return cancel();
		});
	}

	handleCancelSubmit(): void {
		if (this.hasChanges) {
			// open a confirm modal
			this.showConfirmationModal("Discard Changes", "Are you sure you want to discard all the changes?", () => {
				this.resetForm();
			});
		} else {
			this.resetForm();
		}
	}

	handleOpenUpload(input?: HTMLInputElement): void {
		if (input) {
			input.click();
		}
	}

	updateProfileService(): void {
		this.profileService.initUser(false);
	}

	async handleChangeProfile(event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		const rawFile = input?.files?.[0];
		if (!rawFile) {
			return;
		}

		this.loaderService.setLoading(true);
		let prepared: File;
		try {
			prepared = await prepareProfileImageFile(rawFile, "profile-picture");
		} catch (error) {
			this.loaderService.setLoading(false);
			this.resetProfilePictureInput();
			this.showUploadError(error);
			return;
		}
		this.loaderService.setLoading(false);

		this.showConfirmationModal(
			"Confirm",
			"Upload new profile picture?",
			() => this.uploadPreparedProfilePicture(prepared),
			() => this.resetProfilePictureInput(),
		);
	}

	private uploadPreparedProfilePicture(file: File): void {
		this.setProfilePreviewFromFile(file);

		if (isEmpty(this.userId)) {
			return;
		}

		this.loaderService.setLoading(true);
		this.httpService
			.uploadProfile(this.userId, {
				file,
				type: "profile-picture",
				prepared: true,
			})
			.subscribe({
				next: () => {
					this.updateProfileService();
					this.loaderService.setLoading(false);
					this.resetProfilePictureInput();
				},
				error: () => {
					this.loaderService.setLoading(false);
				},
			});
	}

	private setProfilePreviewFromFile(file: File): void {
		this.revokeProfilePreviewObjectUrl();
		this.profilePreviewObjectUrl = URL.createObjectURL(file);
		this.profilePreview = this.profilePreviewObjectUrl;
	}

	private revokeProfilePreviewObjectUrl(): void {
		if (this.profilePreviewObjectUrl) {
			URL.revokeObjectURL(this.profilePreviewObjectUrl);
			this.profilePreviewObjectUrl = undefined;
		}
	}

	private resetProfilePictureInput(): void {
		const input = this.profilePictureRef?.nativeElement;
		if (input) {
			input.value = "";
		}
	}

	private showUploadError(error: unknown): void {
		const message =
			error instanceof Error && error.message ? error.message : PROFILE_IMAGE_UNREADABLE;

		this.dialog.open(CbtaModalComponent, {
			panelClass: "error-modal",
			height: "220px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalName: "error-modal",
				modalTitle: "Error",
				modalContent: message,
				modalBtnConfirmText: "Close",
				noCloseBtn: true,
			},
		});
	}

	handleChangeSignature(event: Event): void {
		const files = (event.target as HTMLInputElement)?.files ?? [];

		if (!isEmpty(files)) {
			const file = files[0];

			// to display the preview
			const reader = new FileReader();

			reader.onload = () => {
				this.signaturePreview = reader.result?.toString();
			};

			reader.readAsDataURL(file);

			// to upload signature into the database
			if (!isEmpty(this.userId)) {
				this.loaderService.setLoading(true);
				this.httpService
					.uploadProfile(this.userId, {
						file,
						type: "signature",
					})
					.subscribe((result) => {
						this.updateProfileService();
						this.loaderService.setLoading(false);
					});
			}
		}
	}

	handleSubmit() {
		// checks if changes has been made
		// Check if the form is valid before submitting
		if (this.profileForm.valid) {
			// Submit your form data here
			if (this.hasChanges) {
				// open a confirm modal
				this.showConfirmationModal("Confirm", "Are you sure you want to submit changes?", () => {
					this.loaderService.setLoading(true);
					// Use getRawValue() to include disabled form controls (non-editable fields)
					const value = this.profileForm.getRawValue();

					if (!isEmpty(this.userId)) {
						this.httpService
							.updateProfile(this.userId, {
								...value,
								rank: value.rank?.code || value.rank,
								licenseType: value.licenseType?.code || value.licenseType,
							})
							.subscribe((result) => {
								this.initUser();
								this.updateProfileService();
							});
					}
					this.loaderService.setLoading(false);
					this.setHasChanges(false);
					this.setEditMode(false);
				});
			} else {
				// if no changes, just close edit mode
				this.setEditMode(false);
			}
		} else {
			// Mark all fields as touched to display validation messages
			this.profileForm.markAllAsTouched();
		}
	}

	showSignatureModal(): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "signature-pad-modal",
			height: "420px",
			width: "528px",
			hasBackdrop: true,
			data: {
				modalTitle: "Signature",
				modalName: "signaturePadModal",
				modalBtnConfirmText: "Submit",
				modalBtnCancelText: "Cancel",
				signatureData: {
					file: this.signatureFile,
					dataUrl: this.signaturePreview,
				},
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result && result.close) {
				const dataUrl = result?.signature?.dataUrl;
				const file = result?.signature?.file;
				const cleared = isEmpty(dataUrl) && isEmpty(file);

				if (cleared) {
					this.signaturePreview = undefined;
					this.signatureFile = undefined;

					if (!isEmpty(this.userId)) {
						this.loaderService.setLoading(true);
						this.httpService.clearProfileSignature(this.userId).subscribe(() => {
							this.updateProfileService();
							this.loaderService.setLoading(false);
						});
					}
				} else if (dataUrl && file) {
					this.signaturePreview = result.signature.dataUrl;

					if (!isEmpty(this.userId)) {
						this.loaderService.setLoading(true);
						this.httpService
							.uploadProfile(this.userId, {
								file,
								type: "signature",
							})
							.subscribe(() => {
								this.updateProfileService();
								this.loaderService.setLoading(false);
							});
					}
				}
			}
			sub.unsubscribe();
		});
	}

	saveRank(data: FormDataOptions) {
		this.rankControl.setValue({
			code: data.code,
			description: data.description,
		});

		this.rankField?.patchValue({
			code: data.code,
			description: data.description,
		});
	}

	saveLicenseType(data: FormDataOptions) {
		const licenseTypeValue = {
			code: data.code,
			description: data.description,
		};

		this.licenseTypeControl.setValue(licenseTypeValue);
		this.licenseTypeField?.patchValue(licenseTypeValue, { emitEvent: true });
	}

	get rankField() {
		return this.profileForm.get("rank");
	}

	get rankOptions() {
		return this.sharedService.dropDownOptionsRank;
	}

	get licenseTypeField() {
		return this.profileForm.get("licenseType");
	}

	get licenseTypeOptions(): FormDataOptions[] {
		return [
			{ code: "ATPL", description: "ATPL" },
			{ code: "CPL", description: "CPL" },
			{ code: "MPL", description: "MPL" },
			{ code: "SPL", description: "SPL" },
		];
	}
}
